import React from 'react';

export type WordCloudDisplayStyle = 'chips' | 'classic' | 'mixed' | 'scattered';

export const WORD_CLOUD_STYLE_OPTIONS: { value: WordCloudDisplayStyle; label: string }[] = [
  { value: 'chips', label: 'Chips' },
  { value: 'classic', label: 'Classic' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'scattered', label: 'Scattered' }
];

type StylePreviewVariant = 'word' | 'emoji';

export const EMOJI_DISPLAY_STYLE_OPTIONS = WORD_CLOUD_STYLE_OPTIONS.filter(
  (option) => option.value !== 'mixed'
);

const MINI_WORDS: Record<WordCloudDisplayStyle, {
  text: string; top: string; left: string; size: number; rotate: number; color: string;
}[]> = {
  chips: [
    { text: 'go', top: '20%', left: '20%', size: 15, rotate: 0, color: '#F29137' },
    { text: 'yes', top: '55%', left: '55%', size: 20, rotate: 0, color: '#04B2FD' },
    { text: 'hi', top: '75%', left: '15%', size: 12, rotate: 0, color: '#E736EB' }
  ],
  classic: [
    { text: 'go', top: '20%', left: '20%', size: 15, rotate: 0, color: '#F29137' },
    { text: 'yes', top: '55%', left: '55%', size: 20, rotate: 0, color: '#04B2FD' },
    { text: 'hi', top: '75%', left: '15%', size: 12, rotate: 0, color: '#E736EB' }
  ],
  mixed: [
    { text: 'go', top: '20%', left: '20%', size: 15, rotate: 0, color: '#F29137' },
    { text: 'yes', top: '50%', left: '55%', size: 18, rotate: 90, color: '#04B2FD' },
    { text: 'hi', top: '75%', left: '15%', size: 12, rotate: 90, color: '#E736EB' }
  ],
  scattered: [
    { text: 'go', top: '20%', left: '20%', size: 15, rotate: -30, color: '#F29137' },
    { text: 'yes', top: '55%', left: '55%', size: 18, rotate: 60, color: '#04B2FD' },
    { text: 'hi', top: '75%', left: '15%', size: 12, rotate: 30, color: '#E736EB' }
  ]
};

const MINI_EMOJIS: Partial<Record<WordCloudDisplayStyle, {
  text: string; top: string; left: string; size: number; rotate: number; color: string;
}[]>> = {
  chips: [
    { text: '😀', top: '20%', left: '20%', size: 15, rotate: 0, color: '#F29137' },
    { text: '🎉', top: '55%', left: '55%', size: 20, rotate: 0, color: '#04B2FD' },
    { text: '❤️', top: '75%', left: '15%', size: 12, rotate: 0, color: '#E736EB' }
  ],
  classic: [
    { text: '😀', top: '20%', left: '20%', size: 15, rotate: 0, color: '#F29137' },
    { text: '🎉', top: '55%', left: '55%', size: 20, rotate: 0, color: '#04B2FD' },
    { text: '❤️', top: '75%', left: '15%', size: 12, rotate: 0, color: '#E736EB' }
  ],
  scattered: [
    { text: '😀', top: '20%', left: '20%', size: 15, rotate: -30, color: '#F29137' },
    { text: '🎉', top: '55%', left: '55%', size: 18, rotate: 60, color: '#04B2FD' },
    { text: '❤️', top: '75%', left: '15%', size: 12, rotate: 30, color: '#E736EB' }
  ]
};

export function WordCloudStylePreview({
  style,
  size = 24,
  variant = 'word'
}: {
  style: WordCloudDisplayStyle;
  size?: number;
  variant?: StylePreviewVariant;
}) {
  const source = variant === 'emoji' ? MINI_EMOJIS : MINI_WORDS;
  const words = source[style] || source.chips || [];
  const hasChip = style === 'chips';
  return (
    <div
      style={{ width: size, height: size, position: 'relative', overflow: 'hidden', borderRadius: 4, background: '#111' }}
    >
      {words.map((w, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: w.top,
            left: w.left,
            transform: `translate(-50%, -50%) rotate(${w.rotate}deg)`,
            fontSize: Math.max(6, Math.round((w.size / 20) * (size / 24) * 10)),
            fontWeight: 700,
            lineHeight: 1,
            color: variant === 'emoji' ? undefined : w.color,
            whiteSpace: 'nowrap',
            ...(hasChip ? {
              backgroundColor: `${w.color}55`,
              padding: '1px 2px',
              borderRadius: 2
            } : {})
          }}
        >
          {w.text}
        </span>
      ))}
    </div>
  );
}

/** Vertical list of style options with a mini preview, used by both the
 * Theme editor's Word Cloud menu and the Ad-hoc/Instant Customization
 * settings panel. Pass variant="emoji" for Emoji Everywhere, which drops
 * "mixed" and shows emoji glyphs instead of text in the mini previews. */
export function WordCloudStylePicker({
  value,
  onChange,
  variant = 'word'
}: {
  value: WordCloudDisplayStyle;
  onChange: (value: WordCloudDisplayStyle) => void;
  variant?: StylePreviewVariant;
}) {
  const options = variant === 'emoji' ? EMOJI_DISPLAY_STYLE_OPTIONS : WORD_CLOUD_STYLE_OPTIONS;
  return (
    <div className='flex flex-col gap-2'>
      {options.map(({ value: optionValue, label }) => {
        const isSelected = (value || 'chips') === optionValue;
        return (
          <button
            key={optionValue}
            type='button'
            onClick={() => onChange(optionValue)}
            className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${isSelected ? 'border-primary bg-primary-50 dark:bg-primary-900/20' : 'border-divider hover:border-default-400'}`}
          >
            <div className='flex-1 flex items-center justify-between'>
              <span className='text-sm'>{label}</span>
              <WordCloudStylePreview style={optionValue} size={32} variant={variant} />
            </div>
          </button>
        );
      })}
    </div>
  );
}
